import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import PhotoUploader from "../components/PhotoUploader";
import { useAuth } from "../context/AuthContext";
import { api, downloadBinary, formatApiError } from "../lib/api";

const ABOUT_MAX = 800;

export default function ProfileEditor() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [completion, setCompletion] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");

  useEffect(() => {
    if (loading) return;
    if (!user || typeof user !== "object") {
      navigate("/login", { replace: true, state: { from: "/profile/edit" } });
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user || typeof user !== "object") return;
    (async () => {
      try {
        const [{ data }, cmp] = await Promise.all([
          api.get("/profile/me"),
          api.get("/profile/me/completion"),
        ]);
        setProfile(data);
        setCompletion(cmp.data);
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
  }, [user]);

  const patch = (obj) => setProfile((prev) => ({ ...prev, ...obj }));
  const patchSection = (section, obj) =>
    setProfile((prev) => ({ ...prev, [section]: { ...prev[section], ...obj } }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/profile/me", profile);
      setProfile(data);
      const cmp = await api.get("/profile/me/completion");
      setCompletion(cmp.data);
      toast.success("Profile saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const addPhoto = async (dataUrl) => {
    if (!dataUrl) return;
    setUploading("adding");
    try {
      const { data } = await api.post("/profile/me/photos", { photo_url: dataUrl });
      setProfile(data);
      toast.success("Photo added");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading("");
    }
  };

  const removePhoto = async (index) => {
    setUploading(`remove-${index}`);
    try {
      const { data } = await api.delete(`/profile/me/photos/${index}`);
      setProfile(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading("");
    }
  };

  if (!profile) {
    return (
      <div>
        <Navbar />
        <div className="container py-5">
          <div className="sb-card" data-testid="profile-loading">Loading your profile…</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <div className="container py-4 py-lg-5" data-testid="profile-editor">
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
          <div>
            <div className="sb-kicker mb-1">Matrimonial profile</div>
            <h1 className="h2 mb-1">Tell your story.</h1>
            <p className="text-muted mb-0">
              Members see this before contact details are shared.
            </p>
          </div>
          <div className="d-flex gap-2 flex-wrap">
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={async () => {
                try {
                  await downloadBinary("/export/pdf", profile, "biodata.pdf");
                  toast.success("PDF downloaded");
                } catch (e) {
                  toast.error(formatApiError(e));
                }
              }}
              data-testid="profile-download-pdf"
            >
              ↓ PDF
            </button>
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={async () => {
                try {
                  await downloadBinary("/export/docx", profile, "biodata.docx");
                  toast.success("DOCX downloaded");
                } catch (e) {
                  toast.error(formatApiError(e));
                }
              }}
              data-testid="profile-download-docx"
            >
              ↓ DOCX
            </button>
            <Link
              to={`/biodata/${profile.id}/edit`}
              className="btn btn-outline-primary"
              data-testid="edit-full-biodata"
            >
              Edit full biodata
            </Link>
            <button className="btn btn-primary" onClick={save} disabled={saving} data-testid="profile-save">
              {saving ? "Saving…" : "Save profile"}
            </button>
          </div>
        </div>

        {/* Completion meter */}
        {completion && (
          <div className="sb-card mb-4" data-testid="completion-meter">
            <div className="d-flex justify-content-between align-items-end mb-2">
              <div>
                <div className="sb-kicker">Profile completion</div>
                <div className="h5 mb-0">{completion.completion}%</div>
              </div>
              <div className="text-muted small">
                {completion.photos} photo{completion.photos !== 1 ? "s" : ""} ·{" "}
                {completion.has_about_me ? "About-me set" : "No about-me yet"}
              </div>
            </div>
            <div className="sb-progress">
              <div className="sb-progress-bar" style={{ width: `${completion.completion}%` }} />
            </div>
          </div>
        )}

        <div className="row g-4">
          {/* Photo gallery */}
          <div className="col-lg-6">
            <div className="sb-card h-100" data-testid="photo-gallery">
              <div className="sb-kicker mb-1">Photo gallery</div>
              <h2 className="h5 mb-3">Up to 6 photos</h2>
              <div className="sb-gallery">
                {(profile.photos || []).map((p, i) => (
                  <div key={i} className="sb-gallery-item" data-testid={`gallery-item-${i}`}>
                    <img src={p} alt={`Gallery ${i + 1}`} />
                    <button
                      type="button"
                      className="sb-gallery-remove"
                      onClick={() => removePhoto(i)}
                      disabled={uploading === `remove-${i}`}
                      data-testid={`gallery-remove-${i}`}
                      aria-label="Remove photo"
                    >
                      ×
                    </button>
                  </div>
                ))}
                {(profile.photos || []).length < 6 && (
                  <div className="sb-gallery-add">
                    <PhotoUploader value="" onChange={addPhoto} />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* About & basic info */}
          <div className="col-lg-6">
            <div className="sb-card h-100">
              <div className="sb-kicker mb-1">About me</div>
              <h2 className="h5 mb-3">A few lines to introduce yourself</h2>
              <textarea
                rows={6}
                className="form-control"
                maxLength={ABOUT_MAX}
                value={profile.lifestyle?.about_me || ""}
                onChange={(e) => patchSection("lifestyle", { about_me: e.target.value })}
                placeholder="I love reading, weekend treks, and Sunday cooking with my mother. Looking for a partner who values family and adventure in equal measure…"
                data-testid="about-me"
              />
              <div className="d-flex justify-content-end small text-muted mt-1">
                {(profile.lifestyle?.about_me || "").length} / {ABOUT_MAX}
              </div>

              <div className="row g-3 mt-2">
                <div className="col-md-6">
                  <label className="form-label">Display name</label>
                  <input
                    className="form-control"
                    value={profile.personal?.full_name || ""}
                    onChange={(e) => patchSection("personal", { full_name: e.target.value })}
                    data-testid="profile-name"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">City</label>
                  <input
                    className="form-control"
                    value={profile.contact?.city || ""}
                    onChange={(e) => patchSection("contact", { city: e.target.value })}
                    data-testid="profile-city"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Occupation</label>
                  <input
                    className="form-control"
                    value={profile.education_career?.occupation || ""}
                    onChange={(e) =>
                      patchSection("education_career", { occupation: e.target.value })
                    }
                    data-testid="profile-occupation"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Discovery</label>
                  <select
                    className="form-select"
                    value={profile.privacy}
                    onChange={(e) => patch({ privacy: e.target.value })}
                    data-testid="profile-privacy"
                  >
                    <option value="private">Private — nobody finds me</option>
                    <option value="members">Members only — verified members can find</option>
                    <option value="shareable">Shareable link — anyone with the link</option>
                    <option value="public">Public — searchable by all</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
